import { useEffect, useState } from "react";
import style from "@/styles/pages/Profile/VerificationCode.module.css";

import { RootState } from "@/store/dtm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useRef } from 'react';
import { setTwilioResultCodeVerified, setResendCounter, setInvalidCounter, setSendFailCounter, setTwilioResult, setCredentialsCreated } from "@/slices/applicantSlice";
import clsx from "clsx";
import AuthCode, { AuthCodeRef } from "react-auth-code";
import { ApplicationDto, CreateTwilioCode, InitIdVerificationDto, RegisterRequest, VerifyTwilioCode } from "@/lib/DTMWebAPIClient.generated";
import { setApplicationErrorCode, setApplicationErrorMessage } from "@/slices/applicationSlice";
import { useAuth } from "@/context/AuthContext";
import SlideTransition from "@/styles/animation/SlideTransition";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setClickInsightEvents, setSingleClickInsightEvent } from "@/lib/util";
import { setAccessToken } from "@/slices/credentialSlice";

export default function JointVerificationCode() {

  const client = createApiClient({ timeout: 60000 });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login } = useAuth();
  const [code, setCode] = useState("");
  const [codeIsValid, setCodeIsValid] = useState("");
  const [codeInputIndicator, setCodeInputIndicator] = useState(0);
  const [verifying, setVerifying] = useState(true);
  const [resendMessage, setResendMessage] = useState(false);
  const [verifyInProgress, setVerifyInProgress] = useState(false);
  const [registrationInProgress, setRegistrationInProgress] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 325);
  const authCodeRef = useRef<AuthCodeRef>(null);
  const currentIndex = useRef(0);

  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const twilioResult = useSelector(
    (state: RootState) => state.applicant.applicant.twilioResult
  );
  const phoneNumber = useSelector(
    (state: RootState) => state.applicant.applicant.phoneNumber
  );
  const sendFailCounter = useSelector(
    (state: RootState) => state.applicant.applicant.sendFailCounter
  );
  const resendCounter = useSelector(
    (state: RootState) => state.applicant.applicant.resendCounter
  );
  const invalidCounter = useSelector(
    (state: RootState) => state.applicant.applicant.invalidCounter
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);

  useEffect(() => {
    window.addEventListener("resize", () => {
      const ismobile = window.innerWidth < 325;
      if (ismobile !== isMobile) setIsMobile(ismobile);
    }, false);
  }, [isMobile]);

  useEffect(() => {
    if (code.length === 6 && !verifyInProgress && currentIndex.current === 6) {
      setVerifyInProgress(true);
      verifyCode(phoneNumber, code);
    }
  }, [code]);

  useEffect(() => {
    document.title = "Verify your phone number";

    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

    authCodeRef.current?.getInput(0).value?.addEventListener('keydown', (e) => advanceCursor(e, 0));
    authCodeRef.current?.getInput(1).value?.addEventListener('keydown', (e) => advanceCursor(e, 1));
    authCodeRef.current?.getInput(2).value?.addEventListener('keydown', (e) => advanceCursor(e, 2));
    authCodeRef.current?.getInput(3).value?.addEventListener('keydown', (e) => advanceCursor(e, 3));
    authCodeRef.current?.getInput(4).value?.addEventListener('keydown', (e) => advanceCursor(e, 4));
    authCodeRef.current?.getInput(5).value?.addEventListener('keydown', (e) => advanceCursor(e, 5));
    authCodeRef.current?.getInput(0).value?.addEventListener('focus', (e) => clearFields());
  }, []);

  const verificationHandler = (e: any) => {
    setCode(e);
  }

  function sendCode(phoneNumber: string, channel: string) {
    client.apiAuthGenerateverificationcode(new CreateTwilioCode({
      verificationType: channel,
      phoneNumber: phoneNumber
    })).then(function (data) {
      if (data) {
        setVerifying(true);
        dispatch(setResendCounter(resendCounter + 1));
        setResendMessage(true);
        setCodeIsValid("");
        setCodeInputIndicator(0);
        setCode("");
      } else {
        dispatch(setSendFailCounter(sendFailCounter + 1));
      }
    });
  }

  function verifyCode(phoneNumber: string, code: string) {
    if (verifying) {
      setResendMessage(false);
      client.apiAuthVerifycode(applicant.alloySessionId, new VerifyTwilioCode({
        phone: phoneNumber,
        code: code,
        attempt: invalidCounter + 1
      })).then(function (result) {
        dispatch(setTwilioResult(result));
        setVerifyInProgress(false);
        if (result) {
          setTwilioResultCodeVerified(result.codeVerified ? result.codeVerified : false);
          dispatch(setAccessToken(result.accessToken != undefined ? result.accessToken : ""));
          result.codeVerified ?
            (setCodeIsValid("true"), 
            setVerifying(false), 
            setCodeInputIndicator(2), 
            setSingleClickInsightEvent("verifyPhoneNumber", "pass")) :
            (setCodeIsValid("false"), setCodeInputIndicator(1),
              dispatch(setInvalidCounter(invalidCounter + 1)),
              authCodeRef.current?.getInput(5).value?.blur(),
              checkInvalidCounter(),
              setSingleClickInsightEvent("verifyPhoneNumber", "fail")
            )
        }
      });
    }
  }

  function advanceCursor(ev: KeyboardEvent, index: number) {
    currentIndex.current = index;
    let isNumber = /^[0-9]$/i.test(ev.key);
    if (isNumber) {
      currentIndex.current = index + 1;
      if (authCodeRef.current?.getInput(index).value?.value === ev.key) {
        authCodeRef.current?.getInput(index).value?.blur();

        //Delay advancing to next cursor to avoid skipping
        const timeout = setTimeout(() => {
          authCodeRef.current?.getInput(index + 1).value?.focus();
        }, 50)
        return () => clearTimeout(timeout)
      }
    }
  }

  function clearFields() {
    setCodeInputIndicator(0);
    authCodeRef.current?.clear();
  }

  function checkInvalidCounter() {
    if (invalidCounter + 1 >= 3) {
      //navigate("/joint/mortgage-details", {replace: true})
      console.log("Failed to validate phone number");
    }
  }

  const createUser = () => {
    //Register user
    setRegistrationInProgress(true);
    let preAuthenticatedClient = createApiClient({
      timeout: 60000,
      token: credential.credential.accessToken,
    });
    preAuthenticatedClient.apiAuthRegister(new RegisterRequest({
      email: credential.credential.email,
      password: credential.credential.password,
      meridianCommunication: credential.credential.meridianCommunication,
      inviteCode: application.jointCode
    })).then(function (data) {
      dispatch(setCredentialsCreated(true));

      let authenticatedClient = createApiClient({ timeout: 60000, token: data.accessToken });
      let localInfo: any = {}
      localInfo.applicant = applicant;
      authenticatedClient.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(localInfo)
      })).catch((error) => {
        console.log("Error saving application info");
      });

      setRegistrationInProgress(false);
      login(data.accessToken != undefined ? data.accessToken : "", data.refreshToken != undefined ? data.refreshToken : "");

      //Call api/idverificaton/init for ID Verification
      if (applicant.alloySessionId != undefined) {
        client.apiIdverificationInit(new InitIdVerificationDto({
          alloySessionId: applicant.alloySessionId,
          applicationId: application.id,
          applicantId: applicant.id,
          status: "PENDING",
          phoneVerified: applicant.twilioResult.codeVerified
        })).then(function (data) {
          navigate("/joint/mortgage-details", { replace: true })
        }).catch((error) => {
          console.log(error);
          navigate("/joint/mortgage-details", { replace: true })
        });
      } else {
        navigate("/joint/mortgage-details", { replace: true })
      }

    }).catch((error) => {
      if (error.response != undefined && error.response.code == 1002) {
        dispatch(setApplicationErrorCode("1002"));
        dispatch(setApplicationErrorMessage(error.response.message));
        navigate("/login", { replace: true });
      }
      setRegistrationInProgress(false);
    });
  }

  return (
    <SlideTransition>
      <main aria-labelledby="main-content">
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
          <div className={style.psHeading}>
            <div className="mb-4">
              <h1>
                Verify your phone number
              </h1>
              <p>
                We've sent a 6-digit code to {phoneNumber}
              </p>
              <p>
                Enter the code to confirm your mobile number
              </p>

            </div>
            <div className="flex">
              {codeIsValid === "true" ?
                <AuthCode
                  onChange={(e) => { }}
                  inputClassName="vi__character--valid"
                  disabled={codeIsValid === "true"}
                />
                :
                <AuthCode
                  onChange={(e) => {
                    let updatedCode = '';
                    e.forEach(char => updatedCode += char);
                    verificationHandler(updatedCode);
                  }}
                  inputClassName={(codeInputIndicator === 0 ? "vi__character" : (codeInputIndicator === 1 ? "vi__character--error" : "vi__character--valid"))}
                  disabled={codeIsValid === "true"}
                  ref={authCodeRef}
                />
              }
            </div>

            <div >
              {codeIsValid === "true" && (
                <div className="flex relative items-start mt-3">
                  <img
                    src="assets/success-icon.svg"
                    className="w-4 h-4 mt-1"
                    alt="error"
                    height={5}
                    width={5}
                  />
                  <span className="flex font-medium tracking-wide success-text text-xs mt-1 ml-1">
                    We have successfully verified your phone number!
                  </span>
                </div>
              )}

            </div>
            <div>
              {codeIsValid === "false" && (
                <div className="flex relative items-start mt-3">
                  <img
                    src="assets/error-icon.svg"
                    className="w-4 h-4 mt-1"
                    alt="error"
                    height={5}
                    width={5}
                  />
                  <span className="flex items-center font-medium tracking-wide error-text text-xs mt-1 ml-1">
                    Invalid code, please try again
                  </span>
                </div>
              )}

            </div>
            {(resendCounter < 3 && invalidCounter < 3 && codeIsValid !== "true") && (
              <div className="mt-5">
                <span>
                  Didn't receive a code?
                  <a onClick={() => sendCode(phoneNumber, "sms")} className="clickText"> Resend Code</a> or
                  <a onClick={() => sendCode(phoneNumber, "call")} className="clickText"> Resend by Voice Call</a>
                </span>

              </div>
            )}


            <div >
              {resendMessage && (
                <div className="flex relative items-start mt-3">
                  <img
                    src="assets/success-icon.svg"
                    className="w-4 h-4 mt-1 ml-5"
                    alt="error"
                    height={5}
                    width={5}
                  />
                  <span className="flex font-medium tracking-wide success-text text-xs mt-1 ml-1">
                    Code sent to phone number ending in {phoneNumber.substring(phoneNumber.length - 4, phoneNumber.length)}
                  </span>
                </div>
              )}

            </div>
            <div className="flex max-sm:hidden">
              <div className="mb-5">
                <button
                  onClick={e =>
                    (codeIsValid === "true" || invalidCounter >= 3 || sendFailCounter >= 3 || resendCounter >= 3)
                      ? createUser()
                      : e.preventDefault()

                  }
                  className={clsx(
                    (codeIsValid === "true" || invalidCounter >= 3 || sendFailCounter >= 3 || resendCounter >= 3)
                      ? "primary mt-5 w-169 sm:w-28 h-11 px-6"
                      : "disabled mt-5 w-169 sm:w-28 h-11 px-6",

                  )}
                  disabled={registrationInProgress}
                >
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(registrationInProgress || verifyInProgress ? "" : "hidden")} />
                  Continue
                </button>
              </div>
            </div>
            <div className="sm:hidden">
              <div className="mb-5">
                <button
                  onClick={e =>
                    (codeIsValid === "true" || invalidCounter >= 3 || sendFailCounter >= 3 || resendCounter >= 3)
                      ? createUser()
                      : e.preventDefault()

                  }
                  className={clsx(
                    (codeIsValid === "true" || invalidCounter >= 3 || sendFailCounter >= 3 || resendCounter >= 3)
                      ? "primary mt-5 w-full h-11 px-6"
                      : "disabled mt-5 w-full h-11 px-6",

                  )}
                  disabled={registrationInProgress}
                >
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(registrationInProgress || verifyInProgress ? "" : "hidden")} />
                  Continue
                </button>
              </div>
            </div>
          </div>

        </div>
      </main>
    </SlideTransition>
  );
}